import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
    FaLock,
    FaShieldAlt,
    FaCreditCard,
    FaCheckCircle,
    FaFileContract,
    FaArrowLeft,
    FaTimesCircle,
} from "react-icons/fa";

export default function PaymentPage() {
    const [searchParams] = useSearchParams();
    const [ready, setReady] = useState(false);
    const [accepted, setAccepted] = useState(false);

    const sessionId = searchParams.get("session");

    /* =========================================================
       LOAD MASTERCARD CHECKOUT
    ========================================================= */

    useEffect(() => {
        if (!sessionId) return;

        const script = document.createElement("script");

        script.src =
            "https://ap-gateway.mastercard.com/static/checkout/checkout.min.js";

        script.setAttribute("data-error", "errorCallback");
        script.setAttribute("data-cancel", "cancelCallback");

        script.onload = () => {
            setReady(true);
        };

        document.body.appendChild(script);

        window.errorCallback = function (error) {
            alert("Payment Error ❌");
            console.log(error);
        };

        window.cancelCallback = function () {
            alert("Payment Cancelled ❌");
        };

        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }

            delete window.errorCallback;
            delete window.cancelCallback;
        };
    }, [sessionId]);

    /* =========================================================
       START PAYMENT
    ========================================================= */

    const startPayment = () => {
        if (!accepted) {
            alert("⚠️ Please accept Terms & Conditions");
            return;
        }

        if (!window.Checkout || !sessionId) {
            alert("Payment not ready");
            return;
        }

        window.Checkout.configure({
            session: {
                id: sessionId,
            },
        });

        window.Checkout.showPaymentPage();
    };

    return (
        <div className="min-h-screen bg-[#F7F5EF] text-[#173F3E]">

            {/* =====================================================
                HERO
            ===================================================== */}

            <section className="relative overflow-hidden bg-[#173F3E] mt-16">

                {/* Decorative circles */}

                <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-[#0B5552]/50 blur-3xl" />

                <div className="pointer-events-none absolute -left-32 bottom-[-140px] h-96 w-96 rounded-full bg-[#B78932]/10 blur-3xl" />

                <div className="pointer-events-none absolute right-[12%] top-1/2 h-44 w-44 -translate-y-1/2 rounded-full border border-white/10" />

                <div className="pointer-events-none absolute right-[14%] top-1/2 h-28 w-28 -translate-y-1/2 rounded-full border border-[#D5A33C]/20" />


                <div className="relative z-10 mx-auto max-w-[1440px] px-5 pb-12 pt-[110px] sm:px-8 sm:pb-14 lg:px-12 lg:pb-16 xl:px-16">

                    <div className="max-w-3xl">

                        {/* Eyebrow */}

                        <div className="flex items-center gap-3">

                            <span className="h-px w-8 bg-[#D5A33C]" />

                            <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#D5A33C]">
                                Secure Checkout
                            </span>

                        </div>


                        {/* Heading */}

                        <h1 className="mt-5 font-serif text-4xl leading-[1.08] text-white sm:text-5xl lg:text-6xl">

                            Complete Your

                            <span className="block text-[#D5A33C]">
                                Payment
                            </span>

                        </h1>


                        {/* Description */}

                        <p className="mt-5 max-w-2xl text-sm leading-7 text-white/65 sm:text-base">

                            You're one step away from completing your booking.
                            Your payment is securely processed through
                            Mastercard Payment Gateway Services.

                        </p>


                        {/* Security points */}

                        <div className="mt-7 flex flex-wrap gap-3">

                            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-xs font-medium text-white/80 backdrop-blur-sm">

                                <FaShieldAlt className="text-[#D5A33C]" />

                                Secure & Encrypted

                            </div>


                            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-xs font-medium text-white/80 backdrop-blur-sm">

                                <FaCreditCard className="text-[#D5A33C]" />

                                Mastercard Gateway

                            </div>

                        </div>

                    </div>

                </div>


                {/* Gold accent */}

                <div className="absolute bottom-0 left-0 h-px w-full bg-[#B78932]" />

            </section>


            {/* =====================================================
                PAYMENT CONTENT
            ===================================================== */}

            <main className="relative overflow-hidden py-12 sm:py-16 lg:py-20">

                {/* Background decoration */}

                <div className="pointer-events-none absolute -right-40 top-20 h-96 w-96 rounded-full bg-[#0B5552]/5 blur-3xl" />

                <div className="pointer-events-none absolute -left-40 bottom-0 h-96 w-96 rounded-full bg-[#B78932]/5 blur-3xl" />


                <div className="relative z-10 mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12">

                    {/* =================================================
                        BACK LINK
                    ================================================= */}

                    <div className="mb-7">

                        <Link
                            to="/"
                            className="group inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition-colors duration-200 hover:text-[#0B5552]"
                        >

                            <FaArrowLeft className="text-xs transition-transform duration-200 group-hover:-translate-x-1" />

                            Back to Home

                        </Link>

                    </div>


                    {/* =================================================
                        MAIN PAYMENT CARD
                    ================================================= */}

                    <div className="grid overflow-hidden rounded-[30px] border border-[#DED8CC] bg-[#FBFAF6] shadow-[0_25px_70px_rgba(23,63,62,0.10)] lg:grid-cols-[0.82fr_1.18fr]">


                        {/* =================================================
                            LEFT — SECURITY PANEL
                        ================================================= */}

                        <div className="relative overflow-hidden bg-[#173F3E] p-7 sm:p-10 lg:p-12">

                            {/* Decorative glow */}

                            <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#0B5552]/60 blur-3xl" />

                            <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#B78932]/10 blur-3xl" />


                            {/* Decorative ring */}

                            <div className="pointer-events-none absolute right-[-30px] top-[45%] h-44 w-44 rounded-full border border-white/5" />


                            <div className="relative z-10">

                                {/* Icon */}

                                <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-[#D5A33C]/30 bg-[#0B5552] text-2xl text-[#D5A33C] shadow-[0_15px_35px_rgba(0,0,0,0.15)]">

                                    <FaLock />

                                </div>


                                {/* Heading */}

                                <h2 className="mt-7 font-serif text-3xl leading-tight !text-white sm:text-4xl">

                                    <span className="block !text-white">Secure</span>


                                    <span className="block text-[#D5A33C]">
                                        Payment
                                    </span>

                                </h2>


                                <p className="mt-4 text-sm leading-7 text-white/60">

                                    Your payment information is protected and
                                    securely processed through Mastercard's
                                    payment infrastructure.

                                </p>


                                {/* Security features */}

                                <div className="mt-9 space-y-6">

                                    {/* Feature 1 */}

                                    <div className="flex items-start gap-4">

                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-[#D5A33C]">

                                            <FaShieldAlt />

                                        </div>

                                        <div>

                                            <p className="text-sm font-semibold text-white">
                                                Secure & Encrypted
                                            </p>

                                            <p className="mt-1 text-xs leading-5 text-white/45">
                                                Your payment data is securely
                                                transmitted and protected.
                                            </p>

                                        </div>

                                    </div>


                                    {/* Feature 2 */}

                                    <div className="flex items-start gap-4">

                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-[#D5A33C]">

                                            <FaCreditCard />

                                        </div>

                                        <div>

                                            <p className="text-sm font-semibold text-white">
                                                Trusted Payment Gateway
                                            </p>

                                            <p className="mt-1 text-xs leading-5 text-white/45">
                                                Payments are processed through
                                                Mastercard Payment Gateway
                                                Services.
                                            </p>

                                        </div>

                                    </div>


                                    {/* Feature 3 */}

                                    <div className="flex items-start gap-4">

                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-[#D5A33C]">

                                            <FaCheckCircle />

                                        </div>

                                        <div>

                                            <p className="text-sm font-semibold text-white">
                                                Safe Checkout
                                            </p>

                                            <p className="mt-1 text-xs leading-5 text-white/45">
                                                Your transaction is handled
                                                securely from start to finish.
                                            </p>

                                        </div>

                                    </div>

                                </div>


                                {/* Security notice */}

                                <div className="mt-9 rounded-2xl border border-white/10 bg-white/5 p-4">

                                    <div className="flex items-start gap-3">

                                        <FaLock className="mt-0.5 shrink-0 text-[#D5A33C]" />

                                        <p className="text-xs leading-5 text-white/50">

                                            We never store your complete card
                                            details on our website.

                                        </p>

                                    </div>

                                </div>

                            </div>

                        </div>


                        {/* =================================================
                            RIGHT — PAYMENT ACTION
                        ================================================= */}

                        <div className="p-7 sm:p-10 lg:p-12">

                            {/* Header */}

                            <div className="mb-8">

                                <div className="mb-3 flex items-center gap-3">

                                    <span className="h-px w-6 bg-[#B78932]" />

                                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0B5552]">

                                        Payment Authorization

                                    </span>

                                </div>


                                <h2 className="font-serif text-3xl leading-tight text-[#173F3E] sm:text-4xl">

                                    Ready to Pay?

                                </h2>


                                <p className="mt-3 max-w-lg text-sm leading-6 text-slate-500">

                                    Please review and accept our terms before
                                    continuing to the secure payment page.

                                </p>

                            </div>


                            {/* =================================================
                                AGREEMENT
                            ================================================= */}

                            <div
                                className={`rounded-[22px] border p-5 transition-all duration-300 ${accepted
                                    ? "border-[#0B5552]/25 bg-[#F0F6F4]"
                                    : "border-[#DED8CC] bg-[#F7F5EF]"
                                    }`}
                            >

                                <label className="flex cursor-pointer items-start gap-4">

                                    {/* Checkbox */}

                                    <div className="relative mt-0.5 shrink-0">

                                        <input
                                            type="checkbox"
                                            checked={accepted}
                                            onChange={() =>
                                                setAccepted(!accepted)
                                            }
                                            className="peer h-5 w-5 cursor-pointer appearance-none rounded-md border-2 border-[#BEB7AA] bg-white transition-all checked:border-[#0B5552] checked:bg-[#0B5552]"
                                        />


                                        <FaCheckCircle
                                            className={`pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[11px] text-white transition-opacity ${accepted
                                                ? "opacity-100"
                                                : "opacity-0"
                                                }`}
                                        />

                                    </div>


                                    {/* Agreement text */}

                                    <div>

                                        <p className="text-sm font-medium leading-6 text-[#173F3E]">

                                            I agree to the{" "}

                                            <Link
                                                to="/terms-conditions"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                                className="font-bold text-[#0B5552] underline decoration-[#B78932] decoration-2 underline-offset-2 transition hover:text-[#173F3E]"
                                            >
                                                Terms & Conditions
                                            </Link>{" "}

                                            and{" "}

                                            <Link
                                                to="/privacy-policy"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                                className="font-bold text-[#0B5552] underline decoration-[#B78932] decoration-2 underline-offset-2 transition hover:text-[#173F3E]"
                                            >
                                                Privacy Policy
                                            </Link>
                                            .
                                        </p>


                                        <p className="mt-2 text-xs leading-5 text-slate-500">

                                            Please read our policies carefully
                                            before proceeding with your
                                            payment.

                                        </p>

                                    </div>

                                </label>

                            </div>


                            {/* =================================================
                                PAYMENT STATUS
                            ================================================= */}

                            <div className="mt-6">

                                {/* Missing session */}

                                {!sessionId ? (

                                    <div className="rounded-[20px] border border-red-200 bg-red-50 p-4">

                                        <div className="flex items-start gap-3">

                                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-red-100 text-sm font-bold text-red-500">

                                                !

                                            </div>

                                            <div>

                                                <p className="text-sm font-bold text-red-700">
                                                    Payment Session Missing
                                                </p>

                                                <p className="mt-1 text-xs leading-5 text-red-600">

                                                    We could not find a valid
                                                    payment session. Please
                                                    return to your booking and
                                                    try again.

                                                </p>

                                            </div>

                                        </div>

                                    </div>

                                ) : !ready ? (

                                    /* Loading */

                                    <div className="rounded-[20px] border border-[#0B5552]/15 bg-[#F0F6F4] p-4">

                                        <div className="flex items-center gap-3">

                                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#0B5552]/20 border-t-[#0B5552]" />

                                            <div>

                                                <p className="text-sm font-bold text-[#173F3E]">
                                                    Preparing Secure Checkout...
                                                </p>

                                                <p className="mt-1 text-xs text-slate-500">
                                                    Please wait while the secure
                                                    payment gateway loads.
                                                </p>

                                            </div>

                                        </div>

                                    </div>

                                ) : (

                                    /* Ready */

                                    <div className="rounded-[20px] border border-[#BFD8C8] bg-[#F0F8F3] p-4">

                                        <div className="flex items-center gap-3">

                                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#DDEEE3] text-[#397A55]">

                                                <FaCheckCircle />

                                            </div>

                                            <div>

                                                <p className="text-sm font-bold text-[#397A55]">
                                                    Secure Checkout Ready
                                                </p>

                                                <p className="mt-1 text-xs text-[#548267]">
                                                    Your secure payment session
                                                    is ready.
                                                </p>

                                            </div>

                                        </div>

                                    </div>

                                )}

                            </div>


                            {/* =================================================
                                PAYMENT BUTTON
                            ================================================= */}

                            <button
                                onClick={startPayment}
                                disabled={!ready}
                                className={`group mt-6 flex w-full items-center justify-center gap-3 rounded-full px-6 py-4 text-sm font-bold transition-all duration-300 ${ready
                                    ? "bg-[#173F3E] text-white shadow-[0_12px_30px_rgba(23,63,62,0.18)] hover:-translate-y-0.5 hover:bg-[#0B5552] hover:shadow-[0_16px_35px_rgba(23,63,62,0.24)]"
                                    : "cursor-not-allowed bg-[#D7D4CC] text-white shadow-none"
                                    }`}
                            >

                                {ready ? (
                                    <>

                                        <FaLock className="text-[#D5A33C]" />

                                        Proceed to Secure Payment

                                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#B78932] text-[#173F3E] transition-transform duration-300 group-hover:translate-x-1">

                                            <FaArrowLeft className="rotate-180 text-[10px]" />

                                        </span>

                                    </>
                                ) : (
                                    <>

                                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />

                                        Preparing Secure Checkout...

                                    </>
                                )}

                            </button>


                            {/* =================================================
                                PAYMENT FOOTER
                            ================================================= */}

                            <div className="mt-7 border-t border-[#DED8CC] pt-6">

                                <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">

                                    <span className="flex items-center gap-2">

                                        <FaLock className="text-[#B78932]" />

                                        Secure Payment

                                    </span>


                                    <span className="hidden h-1 w-1 rounded-full bg-[#CFC8BC] sm:block" />


                                    <span>
                                        Mastercard Gateway
                                    </span>


                                    <span className="hidden h-1 w-1 rounded-full bg-[#CFC8BC] sm:block" />


                                    <span>
                                        Encrypted Checkout
                                    </span>

                                </div>

                            </div>

                        </div>

                    </div>


                    {/* =================================================
                        BOTTOM SECURITY NOTE
                    ================================================= */}

                    <div className="mt-7 flex flex-col items-center justify-center gap-2 text-center sm:flex-row">

                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#E9F0EE] text-[#0B5552]">

                            <FaShieldAlt className="text-xs" />

                        </div>

                        <p className="text-xs leading-5 text-slate-500">

                            Your transaction is securely processed through
                            Mastercard Payment Gateway Services.

                        </p>

                    </div>

                </div>

            </main>

        </div>
    );
}